import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { getPagedContinents } from '@/services/continent.service';
import { Continent } from '@/models/continent.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ContinentDrawer from './continent-drawer';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const ContinentList = () => {
  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Continent[]>([]);
  const [loading, setLoading] = useState(true);
  const [idToEdit, setIdToEdit] = useState<number|undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);
  
  const fetchData = async (req: PaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<Continent> = await getPagedContinents(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Continents:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };
  
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIdToEdit(undefined);
    fetchData(filterReq);
  };
  
  const onEditItem = (id: number)=>{
    setIdToEdit(id);
    handleOpenDrawer();
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={handleOpenDrawer}>
          Add Continent
        </Button>

        <Stack spacing={6}>       
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />        
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >      
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (              
              <IconButton onClick={() => onEditItem(rowData.id)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <ContinentDrawer 
        continentId={idToEdit} 
        open={openDrawer} 
        onClose={handleCloseDrawer} 
      />
    </PageContent>
  );
};

export default ContinentList;
